import { useCallback, useEffect, useRef, useState } from 'react'
import type { InfiniteData } from '@tanstack/react-query'
import type { Message, MessagePage } from './conversations'

/**
 * The live half of a conversation (D-750).
 *
 * **Server-sent events on an ordinary API route**, which is what lets it
 * inherit the module gate, the capability filter, the kiosk boundary and the
 * 404 convention rather than being given each by hand.
 *
 * **It is additive, and that is the safety.** The fifteen-second polls under it
 * are untouched, so a proxy that will not stream, a dropped connection or a
 * second instance all degrade to exactly today's behaviour rather than to
 * silence.
 */

/**
 * Folds messages that arrived live into the transcript's pages.
 *
 * **Appended to the newest page, and never duplicated.** The poll underneath
 * is still running, so the same message can arrive twice — once down the
 * stream and once on the next refetch — and the id is what makes that
 * harmless. Written as a pure function because it is the only part of this
 * file a test can reach: `EventSource` does not exist in jsdom.
 *
 * **Out-of-order arrivals keep the transcript in order.** The server sends in
 * sequence, but a re-render can interleave a push with a refetch, so the tail
 * is sorted by `sentAt` rather than trusted.
 */
export function mergeLive(
  data: InfiniteData<MessagePage> | undefined,
  arriving: Message[],
): InfiniteData<MessagePage> | undefined {
  if (data === undefined || data.pages.length === 0 || arriving.length === 0) return data

  const held = new Set(data.pages.flatMap((page) => page.messages.map((m) => m.id)))
  const fresh = arriving.filter((m) => !held.has(m.id))

  if (fresh.length === 0) return data

  /*
   * <b>Page zero is the newest, not the last one.</b> The transcript pages
   * *backwards* - each Show earlier messages appends an older page - so
   * react-query's array runs newest to oldest, which is why the screen renders
   * it reversed. Appending to the end put a live message three rows up from the
   * bottom, above older ones, and left the newest-message effects (scroll, mark
   * read) pointing at something that had not changed. Found by an audit driving
   * the real cache; both the code and the test fixture had it the wrong way up.
   */
  const pages = [...data.pages]
  const newest = pages[0]!

  pages[0] = {
    ...newest,
    // Plain comparison, not localeCompare: ICU collates '.' before '+', so a
    // whole-second stamp sorts after a fractional one in the same second - and
    // a transcript's order should not depend on the reader's locale.
    messages: [...newest.messages, ...fresh].sort((a, b) => (a.sentAt < b.sentAt ? -1 : 1)),
  }

  return { ...data, pages }
}

/**
 * How long to wait before each reconnection attempt, in milliseconds.
 *
 * **Sized for a deploy, not for a blip.** Every database-backed endpoint
 * refuses for about seventy-five seconds after a restart (D-33), and
 * `EventSource` cannot tell a 502 from a 404 — both leave it `CLOSED` and both
 * stop its own retrying. A ladder that gives up inside that window would mean
 * every reader sitting on a conversation across a morning deploy loses live
 * delivery until she navigates away and back.
 *
 * Exported so a test can drive it without waiting on a real clock.
 */
export const RETRY_LADDER = [2_000, 5_000, 15_000, 30_000, 60_000]

/**
 * Holds a stream open for one conversation.
 *
 * **Reconnects a bounded number of times, then stops.** A non-2xx and a
 * dropped connection look identical from here — `EventSource` exposes no
 * status — so rather than guess, it retries on the ladder above and gives up
 * afterwards. A conversation she has genuinely been taken out of therefore
 * costs a handful of refused requests over two minutes rather than one every
 * few seconds for as long as the tab is open.
 *
 * **Closed while the tab is hidden**, matching the polls underneath, which
 * already stop when the window loses focus. A forgotten tab holds no
 * connection.
 *
 * **Everything it does is an optimisation.** Where there is no `EventSource`,
 * where the stream cannot be opened, or where it has given up, the
 * fifteen-second poll carries the conversation exactly as it does today.
 */
export function useConversationLive(
  id: string,
  onArrive: (messages: Message[]) => void,
  onBehind: () => void,
  onTyping: (names: string[]) => void,
): void {
  useEffect(() => {
    if (id === '') return

    /*
     * jsdom has no EventSource, so without this every test of the conversation
     * screen throws on a feature none of them is about. It is also the honest
     * behaviour anywhere else it is missing: no stream, and the poll carries on.
     */
    if (typeof EventSource === 'undefined') return

    let source: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let attempts = 0
    let abandoned = false

    const close = () => {
      // Cleared first: closing sets readyState to CLOSED, and an error event
      // already queued would otherwise be read as the server refusing us.
      const closing = source
      source = null
      closing?.close()
    }

    const open = () => {
      if (abandoned || source !== null || document.visibilityState === 'hidden') return

      const stream = new EventSource(`/api/conversations/${id}/live`)
      source = stream

      // A stream that opens is a stream that works: the ladder starts again,
      // so an evening of brief drops never exhausts it.
      stream.addEventListener('open', () => {
        attempts = 0
      })

      stream.addEventListener('messages', (event) => {
        try {
          onArrive(JSON.parse((event as MessageEvent).data) as Message[])
        } catch {
          // A frame we cannot read is not worth taking the stream down for;
          // the poll underneath carries the same messages a moment later.
        }
      })

      // Too far behind for the stream to carry: one ordinary refetch rather
      // than a stream trying to become the transcript.
      stream.addEventListener('behind', () => onBehind())

      stream.addEventListener('typing', (event) => {
        try {
          onTyping(JSON.parse((event as MessageEvent).data) as string[])
        } catch {
          // Dots are the least important thing on the screen.
        }
      })

      stream.onerror = () => {
        // Anything but CLOSED is the browser reconnecting on its own, which it
        // does better than we would.
        if (stream.readyState !== EventSource.CLOSED) return

        // Already replaced or torn down: not ours to act on.
        if (source !== stream) return

        close()

        const wait = RETRY_LADDER[attempts];
        attempts += 1

        if (wait === undefined) {
          abandoned = true
          return
        }

        retry = setTimeout(open, wait)
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (retry !== null) clearTimeout(retry)
        retry = null
        close()
        return
      }

      // Shown again is a fresh start: whatever went wrong while it was hidden
      // is not evidence about now.
      attempts = 0
      abandoned = false
      open()
    }

    open()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      abandoned = true
      if (retry !== null) clearTimeout(retry)
      retry = null
      document.removeEventListener('visibilitychange', onVisibility)
      close()
    }
  }, [id, onArrive, onBehind, onTyping])
}

/**
 * How often the composer says she is still typing, in milliseconds.
 *
 * **Throttled, not debounced.** Debouncing would send nothing until she
 * paused, which is the opposite of what the dots are for. The server believes
 * each one for a few seconds, so this only has to be comfortably shorter than
 * that.
 */
export const TYPING_EVERY = 3_000

/**
 * Tells the conversation she is typing, at most every {@link TYPING_EVERY}.
 *
 * **Fire and forget.** A refused or failed call means the dots do not appear
 * for a moment; it must never interrupt somebody writing a message, so nothing
 * here surfaces an error or retries.
 */
export function typingSender(id: string, now: () => number = Date.now) {
  // Negative infinity, not zero: zero means *sent at the epoch*, which is far
  // enough in the past to be harmless with Date.now() and swallows the very
  // first keystroke against any clock that starts near it. Found by the test.
  let last = Number.NEGATIVE_INFINITY

  return () => {
    const at = now()
    if (at - last < TYPING_EVERY) return

    last = at

    void fetch(`/api/conversations/${id}/typing`, { method: 'POST' }).catch(() => {})
  }
}

/**
 * Who is typing, forgotten after a while on the client's own clock.
 *
 * **The client expires them rather than waiting to be told they stopped.**
 * There is no "stopped typing" message to lose: a closed laptop simply stops
 * saying anything, and the dots go out on their own a few seconds later.
 */
export function useTypists(): [string[], (names: string[]) => void] {
  const [typists, setTypists] = useState<string[]>([])
  const forget = useRef<ReturnType<typeof setTimeout> | null>(null)

  const heard = useCallback((names: string[]) => {
    setTypists(names)

    if (forget.current !== null) clearTimeout(forget.current)

    forget.current = names.length === 0 ? null : setTimeout(() => setTypists([]), TYPING_EVERY * 2)
  }, [])

  useEffect(
    () => () => {
      if (forget.current !== null) clearTimeout(forget.current)
    },
    [],
  )

  return [typists, heard]
}

/** How the dots read, in the studio's own words. */
export function typingSays(names: string[]): string | null {
  if (names.length === 0) return null
  if (names.length === 1) return `${names[0]} is typing…`
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`

  return `${names[0]} and ${names.length - 1} others are typing…`
}
